// calculation-actions.ts
import { createAction, emptyProps, props } from '@ngrx/store';
import { CalculationApi, CalculationHeaderApi, CalculationLineApi, CalculationMetaApi, CalculationTailApi, Project } from '../types';
import { Aggregation, CalculationSelection, CalculationState, WarningType } from './calculation-state';
import { Action } from '@ngrx/store';
import { StaleProperty } from './undo-redo';
import { PdfExportOptions } from '../features/pdf-export/types/pdf-export.types';

export interface ErrorAction {
  type: string;
  error: any;
}

export function isErrorAction(action: Action): action is ErrorAction {
  return (action as ErrorAction).error !== undefined;
}

// New calculation
export const createCalculation = createAction(
  '[Calculation] Create Calculation',
  props<{ project: Project, calculationType: string }>()
);

export const createCalculationSuccess = createAction(
  '[Calculation] Create Calculation Success',
  props<{ project: Project, calculation: CalculationApi }>()
);

export const createCalculationFailure = createAction(
  '[Calculation] Create Calculation Failure',
  props<{ error: any }>()
);

export const updateGrandTotals = createAction(
  '[Calculation] Update Grand Totals',
  props<{ aggregation: Aggregation }>()
)

// Copy calculation
export const copyCalculation = createAction(
  '[Calculation] Copy Calculation',
  props<{ projectId: string, calculationId: string, version?: number }>()
);

export const copyCalculationSuccess = createAction(
  '[Calculation] Copy Calculation Success',
  props<{ project: Project, calculation: CalculationApi }>()
);

export const copyCalculationFailure = createAction(
  '[Calculation] Copy Calculation Failure',
  props<{ error: any }>()
);

// Calculation loading
export const loadCalculation = createAction(
  '[Calculation] Load Calculation',
  props<{ projectId: string, calculationId: string, version?: number }>() // Include parameters
);

export const loadCalculationSuccess = createAction(
  '[Calculation] Load Calculation Success',
  props<{ calculation: CalculationApi }>()
);

export const loadCalculationFailure = createAction(
  '[Calculation] Load Calculation Failure',
  props<{ error: any }>()
);

// Reference calculation loading
export const loadRefCalculation = createAction(
  '[Calculation] Load Reference Calculation',
  props<{ projectId: string, calculationId: string, version?: number }>() // Include parameters
);

export const loadRefCalculationSuccess = createAction(
  '[Calculation] Load Reference Calculation Success',
  props<{ calculation: CalculationApi }>()
);

export const loadRefCalculationFailure = createAction(
  '[Calculation] Load Reference Calculation Failure',
  props<{ error: any }>()
);

export const updateSelection = createAction(
  '[Calculation] Update Selection',
  props<{ selection: CalculationSelection }>()
)

export const deselectLine = createAction(
  '[Calculation] Deselect Line'
)

export const insertLines = createAction(
  '[Calculation] Insert Lines',
  props<{ projectId: string, calculationId: string, lines: CalculationLineApi[] }>()
);

export const insertLinesSuccess = createAction(
  '[Calculation] Insert Lines Success',
  props<{ lines: CalculationLineApi[]; meta?: { redoIgnore: boolean } }>()
)

export const insertLinesFailure = createAction(
  '[Calculation] Insert Lines Failure',
  props<{ error: any }>()
);

export const deleteLine = createAction(
  '[Calculation] Delete Line',
  props<{ projectId: string, calculationId: string, line: CalculationLineApi }>()
)

export const deleteLines = createAction(
  '[Calculation] Delete Lines',
  props<{ projectId: string, calculationId: string, lines: CalculationLineApi[] }>()
)

export const deleteLineSuccess = createAction(
  '[Calculation] Delete Line Success',
  props<{ line: CalculationLineApi }>()
)

export const deleteLineFailure = createAction(
  '[Calculation] Delete Line Failure',
  props<{ error: any }>()
);

export const deleteLinesSuccess = createAction(
  '[Calculation] Delete Lines Success',
  props<{ lines: CalculationLineApi[] }>()
)

export const deleteLinesFailure = createAction(
  '[Calculation] Delete Lines Failure',
  props<{ error: any }>()
);

export const undeleteLine = createAction(
  '[Calculation] Undelete Line',
  props<{ projectId: string, calculationId: string, line: CalculationLineApi }>()
)

export const undeleteLineSuccess = createAction(
  '[Calculation] Undelete Line Success',
  props<{ line: CalculationLineApi }>()
)

export const undeleteLineFailure = createAction(
  '[Calculation] Undelete Line Failure',
  props<{ error: any }>()
);

export const undeleteTailLine = createAction(
  '[Calculation] Undelete Tail Line',
  props<{ projectId: string, calculationId: string, line: CalculationTailApi }>()
)

export const undeleteTailLineSuccess = createAction(
  '[Calculation] Undelete Tail Line Success',
  props<{ line: CalculationTailApi }>()
)

export const undeleteTailLineFailure = createAction(
  '[Calculation] Undelete Tail Line Failure',
  props<{ error: any }>()
);

export const updateLineUi = createAction(
  '[Calculation] Update Line Ui',
  props<{ projectId: string, calculationId: string, line: CalculationLineApi }>()
)

export const updateCalculationLineStale = createAction(
  '[Calculation] Update Line Stale',
  props<{ projectId: string, calculationId: string, line: CalculationLineApi }>()
)

export const updateTailLineStale = createAction(
  '[Calculation] Update Tail Line Stale',
  props<{ projectId: string, calculationId: string, line: CalculationTailApi }>()
)

export const updateLineSuccess = createAction(
  '[Calculation] Update Line Success',
  props<{ line: CalculationLineApi }>()
)

export const updateLineFailure = createAction(
  '[Calculation] Update Line Failure',
  props<{ error: any }>()
);

export const insertLine = createAction(
  '[Calculation] Insert Line',
  props<{ projectId: string, calculationId: string, line: CalculationLineApi }>()
);

export const insertLineSuccess = createAction(
  '[Calculation] Insert Line Success',
  props<{ line: CalculationLineApi; meta?: { redoIgnore: boolean }  }>()
)

export const insertLineFailure = createAction(
  '[Calculation] Insert Line Failure',
  props<{ error: any }>()
);

// TAIL
export const deleteTailLine = createAction(
  '[Calculation] Delete Tail Line',
  props<{ projectId: string, calculationId: string, line: CalculationTailApi }>()
)

export const deleteTailLineSuccess = createAction(
  '[Calculation] Delete Tail Line Success',
  props<{ line: CalculationTailApi }>()
)

export const deleteTailLineFailure = createAction(
  '[Calculation] Delete Tail Line Failure',
  props<{ error: any }>()
);
export const deleteTailLines = createAction(
  '[Calculation] Delete Tail Lines',
  props<{ projectId: string, calculationId: string, lines: CalculationTailApi[] }>()
)

export const deleteTailLinesSuccess = createAction(
  '[Calculation] Delete Tail Lines Success',
  props<{ lines: CalculationTailApi[] }>()
)

export const deleteTailLinesFailure = createAction(
  '[Calculation] Delete Tail Lines Failure',
  props<{ error: any }>()
);

export const updateTailLineUi = createAction(
  '[Calculation] Update Tail Line Ui',
  props<{ projectId: string, calculationId: string, line: CalculationTailApi }>()
)

export const updateTailLineSuccess = createAction(
  '[Calculation] Update Tail Line Success',
  props<{ line: CalculationTailApi }>()
)

export const updateTailLineFailure = createAction(
  '[Calculation] Update Tail Line Failure',
  props<{ error: any }>()
);

export const bulkInsertTailLines = createAction(
  '[Calculation] Bulk Insert Tail Lines',
  props<{ lines: CalculationTailApi[] }>()
)

export const bulkInsertTailLinesSuccess = createAction(
  '[Calculation] Bulk Insert Tail Lines Success',
  props<{ lines: CalculationTailApi[] }>()
)

export const bulkInsertTailLinesFailure = createAction(
  '[Calculation] Bulk Insert Tail Lines Failure',
  props<{ error: any }>()
);

export const bulkUpdateTailLines = createAction(
  '[Calculation] Bulk update Tail Lines',
  props<{ lines: CalculationTailApi[] }>()
)

export const bulkUpdateTailLinesSuccess = createAction(
  '[Calculation] Bulk Update Tail Lines Success',
  props<{ lines: CalculationTailApi[] }>()
)

export const bulkUpdateTailLinesFailure = createAction(
  '[Calculation] Bulk Update Tail Lines Failure',
  props<{ error: any }>()
);

export const insertTailLine = createAction(
  '[Calculation] Insert Tail Line',
  props<{ projectId: string, calculationId: string, line: CalculationTailApi }>()
);

export const insertTailLineSuccess = createAction(
  '[Calculation] Insert Tail Line Success',
  props<{ line: CalculationTailApi }>()
)

export const insertTailLineFailure = createAction(
  '[Calculation] Insert Tail Line Failure',
  props<{ error: any }>()
);

export const insertHeaderLine = createAction(
  '[Calculation] Insert Header Line',
  props<{ projectId: string, calculationId: string, line: CalculationHeaderApi }>()
);

export const insertHeaderLineSuccess = createAction(
  '[Calculation] Insert Header Line Success',
  props<{ line: CalculationLineApi[] }>()
)

export const insertHeaderLineFailure = createAction(
  '[Calculation] Insert Header Line Failure',
  props<{ error: any }>()
);

export const updateVisibleParts = createAction(
  '[Calculation] Update Visible Parts',
  props<{ show: 'calculation' | 'all' | 'tail' }>()
)

export const showSideMenu = createAction(
  '[Calculation] Show Side Menu'
)

export const hideSideMenu = createAction(
  '[Calculation] Hide Side Menu'
)

// META ==========================================================================
/**
 * Update the meta object of the calculation
 */
export const updateMeta = createAction(
  '[Calculation] Update Meta',
  props<{ projectId: string, calculationId: string, meta: CalculationMetaApi }>()
);

/**
 * Handle successful update of meta object
 */
export const updateMetaSuccess = createAction(
  '[Calculation] Update Meta Success',
  props<{ projectId: string, calculationId: string, meta: CalculationMetaApi }>()
)

/**
 * Handle unsuccessful update of meta object
 */
export const updateMetaFailure = createAction(
  '[Calculation] Update Meta Failure',
  props<{ error: any }>()
);

/**
 * The meta object is updated as a whole
 * There is one setting in the object that might affect existing calculation lines
 * When net provisional costs are no longer allowed, all exsiting provisional cost lines have to be
 * updated to gross provisional costs. The difference is that net costs are not included in the calculation
 * of tail costs
 * So when the setting is updated from true to false, before updating the meta object, all cost lines
 * containing net provisional costs have to be updated to gross. This requires bulk update of these
 */
export const updateMetaProvisionalSetting = createAction(
  '[Calculation] Update Meta Provisional Setting',
  props<{ projectId: string, calculationId: string, meta: CalculationMetaApi }>()
);

export const updateWarnings = createAction(
  '[Calculation] Update Warnings',
  props<{ warnings: WarningType[] }>()
);

export const freezeCalculation = createAction(
  '[Calculation] Freeze Calculation',
  props<{ projectId: string, calculationId: string, version?: number }>()
);

export const freezeCalculationSuccess = createAction(
  '[Calculation] Freeze Calculation Success',
  props<{ meta: CalculationMetaApi }>()
);

export const freezeCalculationFailure = createAction(
  '[Calculation] Freeze Calculation Failure',
  props<{ error: any }>()
);

export const variationsFromCalculation = createAction(
  '[Calculation] Variations From Calculation',
  props<{ projectId: string, calculationId: string }>()
);

// export const variationsFromCalculationSuccess = createAction(
//   '[Calculation] Variations From Calculation Success',
//   props<{ projectId: string, calculationId: string }>()
// );

export const variationsFromCalculationFailure = createAction(
  '[Calculation] Variations From Calculation Failure',
  props<{ error: any }>()
);

export const unlockCalculation = createAction(
  '[Calculation] Unlock Calculation',
  props<{ projectId: string, calculationId: string }>()
);

export const unlockCalculationSuccess = createAction(
  '[Calculation] Unlock Calculation Success',
  props<{ projectId: string, calculationId: string }>()
);

export const unlockCalculationFailure = createAction(
  '[Calculation] Unlock Calculation Failure',
  props<{ error: any }>()
);

export const selectCategory = createAction(
  '[Calculation] Select Category',
  props<{ code: string }>()
);

export const moveSelectionUp = createAction(
  '[Calculation] Move Selection Up'
);

export const moveSelectionRight = createAction(
  '[Calculation] Move Selection Right'
);

export const moveSelectionDown = createAction(
  '[Calculation] Move Selection Down'
);

export const moveSelectionLeft = createAction(
  '[Calculation] Move Selection Left'
);

export const calculationUndo = createAction(
  '[Calculation] Undo',
  props<{ diffs: StaleProperty[], reversedState: CalculationState }>()
);

export const calculationUndoRedoDone = createAction(
  '[Calculation] Undo Redo Done',
  props<{ reversedState: CalculationState }>()
);

export const calculationUndoRedoFailure = createAction(
  '[Calculation] Undo Redo Failure',
  props<{ error: any }>()
);

export const calculationRedo = createAction(
  '[Calculation] Redo',
  props<{ diffs: StaleProperty[], reversedState: CalculationState }>()
);

export const bulkUpdateLines = createAction(
  '[Calculation] Bulk Update Lines',
  props<{ lines: CalculationLineApi[] }>()
)

export const bulkUpdateLinesSuccess = createAction(
  '[Calculation] Bulk Update Lines Success',
  props<{ lines: CalculationLineApi[] }>()
)

export const bulkUpdateLinesFailure = createAction(
  '[Calculation] Bulk Update Lines Failure',
  props<{ error: any }>()
);

export const bulkDeleteLinesFailure = createAction(
  '[Calculation] Bulk Delete Lines Failure',
  props<{ error: any }>()
);

export const bulkUndeleteLinesFailure = createAction(
  '[Calculation] Bulk Undelete Lines Failure',
  props<{ error: any }>()
);

export const pdfExport = createAction(
  '[Calculation] Pdf Export',
  props<{options: PdfExportOptions}>()
)

export const pdfExportCreationSuccess = createAction(
  '[Calculation] Pdf Export Creation Success',
  props<{file: any, options: PdfExportOptions}>()
)

export const pdfExportDownloadSuccess = createAction(
  '[Calculation] Pdf Export Download Success',
)

export const pdfExportFailure = createAction(
  '[Calculation] Pdf Export Failure',
  props<{error: any}>()
)

export const startScrolling = createAction(
  '[Calculation] Start Scrolling'
);

export const updateTableLayout = createAction(
  '[Calculation] Update Table Layout',
  props<{ layout: 'default' | 'condensed' }>()
);
